import { useEffect, useRef, useState } from 'react';

const TYPEWRITER_INTERVAL_MS = 45;

const typedContentCache = new Set<string>();

export const isContentAlreadyTyped = (content: string): boolean =>
    typedContentCache.has(content);

export const markTextAsTyped = (content: string): void => {
    if (content.trim() !== '') {
        typedContentCache.add(content);
    }
};

export const clearTypedCache = (content: string): void => {
    typedContentCache.delete(content);
};

const CHAR_TYPE_TARGET_TOTAL_MS = 700;
const CHAR_TYPE_MIN_CHAR_MS = 22;
const CHAR_TYPE_MAX_CHAR_MS = 55;

interface UseCharacterTypewriterOptions {
    text: string;
    enabled?: boolean;
    skipAnimation?: boolean;
    onComplete?: () => void;
}

export const useCharacterTypewriter = ({
    text,
    enabled = true,
    skipAnimation = false,
    onComplete,
}: UseCharacterTypewriterOptions) => {
    const alreadyTyped = skipAnimation || typedContentCache.has(text);
    const [visibleCount, setVisibleCount] = useState(
        alreadyTyped ? text.length : 0,
    );
    const completedTextRef = useRef<string | null>(alreadyTyped ? text : null);
    const lastFiredCallbackRef = useRef<(() => void) | undefined>(undefined);

    if (alreadyTyped && visibleCount < text.length) {
        setVisibleCount(text.length);
    }

    useEffect(() => {
        if (!enabled || alreadyTyped || text.length === 0) {
            return undefined;
        }
        if (completedTextRef.current === text) {
            setVisibleCount(text.length);
            return undefined;
        }

        const perChar = Math.min(
            CHAR_TYPE_MAX_CHAR_MS,
            Math.max(
                CHAR_TYPE_MIN_CHAR_MS,
                CHAR_TYPE_TARGET_TOTAL_MS / text.length,
            ),
        );
        const startedAt = Date.now();
        let rafId = 0;

        const tick = () => {
            const elapsed = Date.now() - startedAt;
            const count = Math.min(
                text.length,
                Math.floor(elapsed / perChar) + 1,
            );
            setVisibleCount(count);
            if (count < text.length) {
                rafId = requestAnimationFrame(tick);
            } else {
                completedTextRef.current = text;
                typedContentCache.add(text);
            }
        };
        rafId = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(rafId);
        };
    }, [text, enabled, alreadyTyped]);

    const isTypingDone =
        enabled && visibleCount >= text.length && text.trim() !== '';

    useEffect(() => {
        if (
            isTypingDone &&
            onComplete != null &&
            lastFiredCallbackRef.current !== onComplete
        ) {
            lastFiredCallbackRef.current = onComplete;
            onComplete();
        }
    }, [isTypingDone, onComplete]);

    return {
        visibleText: text.slice(0, visibleCount),
        isDone: isTypingDone,
    };
};

interface UseTypewriterOptions {
    text: string;
    enabled?: boolean;
    skipAnimation?: boolean;
    /** Text is still arriving (e.g. live SSE stream) — don't cache it as "already typed". */
    isStreaming?: boolean;
    onComplete?: () => void;
}

export const useTypewriter = ({
    text,
    enabled = true,
    skipAnimation = false,
    isStreaming = false,
    onComplete,
}: UseTypewriterOptions) => {
    const words = text.split(' ');
    const alreadyTyped = skipAnimation || typedContentCache.has(text);
    const [visibleWordCount, setVisibleWordCount] = useState(
        alreadyTyped ? words.length : 0,
    );

    if (alreadyTyped && visibleWordCount < words.length) {
        setVisibleWordCount(words.length);
    }

    const targetWordCountRef = useRef(words.length);
    targetWordCountRef.current = words.length;
    const lastFiredCallbackRef = useRef<(() => void) | undefined>(undefined);

    const isTypingDone =
        enabled && visibleWordCount >= words.length && text.trim() !== '';

    useEffect(() => {
        if (!enabled || alreadyTyped) return undefined;

        const interval = setInterval(() => {
            setVisibleWordCount((c) => {
                if (c >= targetWordCountRef.current) {
                    return c;
                }
                return c + 1;
            });
        }, TYPEWRITER_INTERVAL_MS);

        return () => {
            clearInterval(interval);
        };
    }, [enabled, alreadyTyped]);

    useEffect(() => {
        if (isStreaming) return;
        if (visibleWordCount >= words.length && text.trim() !== '') {
            typedContentCache.add(text);
        }
    }, [visibleWordCount, words.length, text, isStreaming]);

    useEffect(() => {
        if (
            isTypingDone &&
            onComplete != null &&
            lastFiredCallbackRef.current !== onComplete
        ) {
            lastFiredCallbackRef.current = onComplete;
            onComplete();
        }
    }, [isTypingDone, onComplete]);

    return {
        visibleText: words.slice(0, visibleWordCount).join(' '),
        isDone: isTypingDone,
    };
};
